import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Users,
  FileText,
  Clock,
  TrendingUp,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  MessageSquare,
  Briefcase,
  Calendar,
  ChevronRight,
  Sparkles,
  Activity,
} from "lucide-react";

const AdminDashboard = () => {
  const { user, token, API, isSuperAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [applications, setApplications] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchData();
    fetchProfile();
    fetchQuickActions();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [
        fetch(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/my-projects`, { headers: { Authorization: `Bearer ${token}` } }),
      ];
      if (isSuperAdmin) {
        promises.push(
          fetch(`${API}/admin/applications`, { headers: { Authorization: `Bearer ${token}` } })
        );
      }
      const responses = await Promise.all(promises);
      if (responses[0].ok) setClients(await responses[0].json());
      if (responses[1].ok) setProjects(await responses[1].json());
      if (isSuperAdmin && responses[2]?.ok) setApplications(await responses[2].json());
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API}/admin/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setProfile(await res.json());
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchQuickActions = async () => {
    try {
      const convRes = await fetch(`${API}/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (convRes.ok) {
        const convData = await convRes.json();
        setRecentMessages(
          convData
            .filter((c) => c.last_message)
            .map((c) => ({
              partner_id: c.partner?.user_id,
              partner_name: c.partner?.name || c.partner?.email || "Unknown",
              last_message: c.last_message?.content || "",
              last_message_time: c.last_message?.created_at,
              unread_count: c.unread_count || 0,
            }))
            .sort((a, b) => new Date(b.last_message_time) - new Date(a.last_message_time))
            .slice(0, 4)
        );
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const profileCompletion = useMemo(() => {
    const fields = [
      { key: "bio", label: "Bio" },
      { key: "photo_updated_at", label: "Photo" },
      { key: "phone", label: "Phone" },
      { key: "linkedin", label: "LinkedIn" },
    ];
    if (!profile)
      return { percentage: 0, missing: fields.map((f) => f.label), total: fields.length };
    const completed = fields.filter((f) => {
      const v = profile[f.key];
      return typeof v === "string" ? v.trim().length > 0 : !!v;
    });
    return {
      percentage: Math.round((completed.length / fields.length) * 100),
      missing: fields
        .filter((f) => {
          const v = profile[f.key];
          return typeof v === "string" ? !v.trim() : !v;
        })
        .map((f) => f.label),
      total: fields.length,
    };
  }, [profile]);

  const activeProjects = projects.filter((p) => p.status === "active").length;
  const pendingApps = applications.filter(
    (a) => a.status === "pending_payment" || a.status === "pending"
  ).length;

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  const todayFormatted = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return (
    <AdminLayout>
      <div className="max-w-[1200px] mx-auto">
        {/* Hero Banner */}
        <div
          className="relative rounded-2xl overflow-hidden mb-8 bg-gradient-to-r from-rnb-navy via-[#1e3a5f] to-rnb-blue"
          data-testid="dashboard-hero"
        >
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-72 h-72 bg-rnb-teal rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl" />
            <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-white rounded-full translate-y-1/2 blur-2xl" />
          </div>
          <div className="relative px-8 py-7 flex items-center justify-between">
            <div>
              <h1
                className="font-heading text-2xl font-bold text-white tracking-tight"
                data-testid="dashboard-greeting"
              >
                {greeting}, {user?.name?.split(" ")[0] || "there"}
              </h1>
              <div className="flex items-center gap-2 mt-1.5">
                <Calendar className="w-3.5 h-3.5 text-white/40" />
                <p className="text-sm text-white/60">{todayFormatted}</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <Link to="/admin/projects">
                <Button
                  size="sm"
                  className="bg-white/15 hover:bg-white/25 text-white border border-white/20 backdrop-blur-sm rounded-lg gap-1.5 text-xs h-9"
                  data-testid="view-projects-btn"
                >
                  <Briefcase className="w-3.5 h-3.5" />
                  Projects
                </Button>
              </Link>
              <Link to="/admin/users">
                <Button
                  size="sm"
                  className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-lg gap-1.5 text-xs h-9"
                  data-testid="manage-users-btn"
                >
                  <Users className="w-3.5 h-3.5" />
                  Manage Users
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-rnb-teal/30 border-t-rnb-teal rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stat Cards — colorful, not boxy */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
              <Link
                to="/admin/clients"
                className="group flex items-center gap-4 bg-white rounded-xl p-5 hover:shadow-lg transition-all duration-300 cursor-pointer"
                data-testid="stat-total-clients"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rnb-blue to-rnb-navy flex items-center justify-center shadow-sm">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {isSuperAdmin ? "Total Clients" : "My Clients"}
                  </p>
                  <p className="font-accent text-2xl font-bold text-rnb-navy">{clients.length}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-rnb-blue group-hover:translate-x-1 transition-all" />
              </Link>

              <Link
                to="/admin/projects"
                className="group flex items-center gap-4 bg-white rounded-xl p-5 hover:shadow-lg transition-all duration-300 cursor-pointer"
                data-testid="stat-active-projects"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rnb-teal to-emerald-500 flex items-center justify-center shadow-sm">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Active Projects</p>
                  <div className="flex items-baseline gap-1.5">
                    <p className="font-accent text-2xl font-bold text-rnb-navy">{activeProjects}</p>
                    <span className="text-xs text-gray-400">of {projects.length}</span>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-rnb-teal group-hover:translate-x-1 transition-all" />
              </Link>

              {isSuperAdmin ? (
                <Link
                  to="/admin/applications"
                  className="group flex items-center gap-4 bg-white rounded-xl p-5 hover:shadow-lg transition-all duration-300 cursor-pointer"
                  data-testid="stat-pending-applications"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Pending Apps</p>
                    <div className="flex items-baseline gap-1.5">
                      <p className="font-accent text-2xl font-bold text-rnb-navy">{pendingApps}</p>
                      <span className="text-xs text-gray-400">of {applications.length}</span>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
                </Link>
              ) : (
                <div
                  className="flex items-center gap-4 bg-white rounded-xl p-5"
                  data-testid="stat-hours-this-month"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Hours This Month</p>
                    <p className="font-accent text-2xl font-bold text-rnb-navy">
                      {clients.reduce((s, c) => s + (c.hours_used || 0), 0).toFixed(1)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Profile Completion — inline, vibrant */}
            {profileCompletion.percentage < 100 && (
              <div
                className="flex items-center gap-4 bg-gradient-to-r from-rnb-blue/5 to-rnb-teal/5 border border-rnb-blue/10 rounded-xl px-5 py-4 mb-8"
                data-testid="profile-completion-card"
              >
                <div className="relative w-11 h-11 shrink-0">
                  <svg className="w-11 h-11 -rotate-90" viewBox="0 0 44 44">
                    <circle cx="22" cy="22" r="18" fill="none" stroke="#e2e8f0" strokeWidth="3.5" />
                    <circle
                      cx="22" cy="22" r="18" fill="none" stroke="#1FC6B7" strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeDasharray={`${(profileCompletion.percentage / 100) * 113} 113`}
                      className="transition-all duration-700"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-rnb-navy">
                    {profileCompletion.percentage}%
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-rnb-navy">Complete your profile</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Missing: {profileCompletion.missing.join(", ")}
                  </p>
                </div>
                <Link to="/admin/settings">
                  <Button
                    size="sm"
                    className="text-xs bg-rnb-teal hover:bg-rnb-teal/90 text-white gap-1 h-8 rounded-lg"
                    data-testid="complete-profile-btn"
                  >
                    Complete
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </Button>
                </Link>
              </div>
            )}

            {/* Main Grid — asymmetric, less boxy */}
            <div className="grid lg:grid-cols-5 gap-6">
              {/* Left Column */}
              <div className="lg:col-span-3 space-y-6">
                {/* Recent Clients — no heavy card wrapper */}
                <div data-testid="recent-clients">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-rnb-navy flex items-center gap-2">
                      <Users className="w-4 h-4 text-rnb-blue" />
                      {isSuperAdmin ? "Recent Clients" : "My Clients"}
                    </h2>
                    <Link to="/admin/clients" className="text-xs text-rnb-blue hover:text-rnb-navy font-medium flex items-center gap-1 transition-colors" data-testid="view-all-clients-btn">
                      View all <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                  <div className="space-y-2">
                    {clients.length > 0 ? (
                      clients.slice(0, 5).map((client) => (
                        <Link
                          key={client.client_id}
                          to={`/admin/clients/${client.client_id}`}
                          className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 hover:shadow-md transition-all duration-200 group"
                          data-testid={`client-row-${client.client_id}`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rnb-navy to-rnb-blue flex items-center justify-center text-white text-xs font-semibold shrink-0">
                            {(client.name || client.user?.name || "?").charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-rnb-navy truncate">
                              {client.name || client.user?.name || "Unknown"}
                            </p>
                            <p className="text-xs text-gray-400 truncate">
                              {client.company_name || client.email || client.user?.email}
                            </p>
                          </div>
                          <Badge
                            className={`text-[10px] px-2 py-0.5 ${
                              client.status === "active"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {client.status || "active"}
                          </Badge>
                          <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-rnb-blue group-hover:translate-x-0.5 transition-all" />
                        </Link>
                      ))
                    ) : (
                      <div className="bg-white rounded-xl py-10 text-center">
                        <p className="text-sm text-gray-400">No clients yet</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Recent Applications or Hours */}
                {isSuperAdmin ? (
                  <div data-testid="recent-applications">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-sm font-semibold text-rnb-navy flex items-center gap-2">
                        <FileText className="w-4 h-4 text-amber-500" />
                        Recent Applications
                      </h2>
                      <Link to="/admin/applications" className="text-xs text-rnb-blue hover:text-rnb-navy font-medium flex items-center gap-1 transition-colors" data-testid="view-all-apps-btn">
                        View all <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                    <div className="space-y-2">
                      {applications.length > 0 ? (
                        applications.slice(0, 5).map((app) => (
                          <div
                            key={app.application_id}
                            className="flex items-center gap-3 bg-white rounded-xl px-4 py-3"
                            data-testid={`app-row-${app.application_id}`}
                          >
                            <div
                              className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                app.status === "enrolled"
                                  ? "bg-emerald-500"
                                  : app.status === "pending" || app.status === "pending_payment"
                                  ? "bg-amber-400"
                                  : "bg-gray-300"
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-rnb-navy truncate">{app.applicant_name}</p>
                              <p className="text-xs text-gray-400">{app.program_type.replace(/_/g, " ")}</p>
                            </div>
                            <Badge
                              className={`text-[10px] px-2 py-0.5 ${
                                app.status === "enrolled"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50"
                                  : app.status === "pending" || app.status === "pending_payment"
                                  ? "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {app.status?.replace("_", " ")}
                            </Badge>
                            <span className="text-[10px] text-gray-400">{formatDate(app.created_at)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="bg-white rounded-xl py-10 text-center">
                          <p className="text-sm text-gray-400">No applications yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div data-testid="hours-summary">
                    <h2 className="text-sm font-semibold text-rnb-navy flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-violet-500" />
                      Hours Summary
                    </h2>
                    <div className="space-y-2">
                      {clients.slice(0, 5).map((client) => (
                        <div key={client.client_id} className="flex items-center justify-between bg-white rounded-xl px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-rnb-navy">{client.name || client.user?.name || "Unknown"}</p>
                            <p className="text-xs text-gray-400">{client.monthly_hours || 0} hrs/mo</p>
                          </div>
                          <p className="font-accent text-sm font-bold text-rnb-navy">{(client.hours_used || 0).toFixed(1)}h</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="lg:col-span-2 space-y-6">
                {/* Quick Links */}
                <div data-testid="quick-links">
                  <h2 className="text-sm font-semibold text-rnb-navy flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-rnb-teal" />
                    Quick Links
                  </h2>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: "Projects", path: "/admin/projects", icon: Briefcase, gradient: "from-rnb-teal to-emerald-500" },
                      { label: "Invoices", path: "/admin/invoices", icon: FileText, gradient: "from-amber-400 to-orange-500" },
                      { label: "Chat", path: "/admin/chat", icon: MessageSquare, gradient: "from-rnb-blue to-rnb-navy" },
                      { label: "Reports", path: "/admin/consultant-hours", icon: TrendingUp, gradient: "from-violet-500 to-purple-600" },
                    ].map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 hover:shadow-md transition-all duration-200 group"
                        data-testid={`quick-link-${link.label.toLowerCase()}`}
                      >
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${link.gradient} flex items-center justify-center`}>
                          <link.icon className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-xs font-medium text-gray-600 group-hover:text-rnb-navy transition-colors">
                          {link.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Messages */}
                <div data-testid="recent-messages">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-rnb-navy flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-rnb-blue" />
                      Messages
                    </h2>
                    <Link to="/admin/chat" className="text-xs text-rnb-blue hover:text-rnb-navy font-medium flex items-center gap-1 transition-colors" data-testid="view-all-messages-btn">
                      Open <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                  <div className="space-y-2">
                    {recentMessages.length > 0 ? (
                      recentMessages.map((conv) => (
                        <Link
                          key={conv.partner_id}
                          to={`/admin/chat/${conv.partner_id}`}
                          className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 hover:shadow-md transition-all duration-200 group"
                          data-testid={`message-row-${conv.partner_id}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rnb-blue to-rnb-teal flex items-center justify-center text-white text-[11px] font-semibold shrink-0">
                            {conv.partner_name?.charAt(0) || "?"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-rnb-navy truncate">{conv.partner_name}</p>
                            <p className="text-xs text-gray-400 truncate">{conv.last_message}</p>
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="w-5 h-5 bg-rnb-teal text-white text-[10px] font-semibold rounded-full flex items-center justify-center">
                              {conv.unread_count}
                            </span>
                          )}
                        </Link>
                      ))
                    ) : (
                      <div className="bg-white rounded-xl py-8 text-center">
                        <MessageSquare className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                        <p className="text-xs text-gray-400">No messages yet</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Profile Complete */}
                {profileCompletion.percentage === 100 && (
                  <div
                    className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-rnb-teal/10 border border-emerald-200/50 rounded-xl px-4 py-3.5"
                    data-testid="profile-completion-card"
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-emerald-700">Profile complete</p>
                      <p className="text-[10px] text-emerald-600/70">All set and visible to clients</p>
                    </div>
                  </div>
                )}

                {/* Activity hint */}
                <Link
                  to="/admin/analytics"
                  className="flex items-center gap-3 bg-gradient-to-r from-rnb-navy to-rnb-blue rounded-xl px-4 py-4 group hover:shadow-lg transition-all duration-300"
                  data-testid="analytics-link"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">View Analytics</p>
                    <p className="text-xs text-white/60">Revenue, clients, and trends</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
